import { HttpErrorResponse, HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { Injector, inject } from '@angular/core';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { tap } from 'rxjs';
import { apiErrorFallback, extractApiErrorMessage } from '../http/extract-api-error-message';
import { SILENT_HTTP_ERRORS } from '../http/silent-http-errors';
import { Translation } from '../i18n/translation.service';
import { PerformanceLineManagerService } from '../services/performance-line-manager.service';

function errorStatusCode(error: HttpErrorResponse): number {
  if (error.status) {
    return error.status;
  }
  const body = error.error;
  if (body && typeof body === 'object' && 'statusCode' in body) {
    return Number((body as { statusCode?: number }).statusCode) || error.status;
  }
  return error.status;
}

function isWorkNestEnvelope(body: object): boolean {
  return 'success' in body || 'data' in body || 'statusCode' in body;
}

function formatErrors(errors: unknown): string | null {
  if (errors === null || errors === undefined) return null;
  if (typeof errors === 'string') return errors.trim() || null;
  if (Array.isArray(errors)) {
    const detail = errors.map(String).filter((item) => item.trim().length > 0).join('\n');
    return detail || null;
  }
  return JSON.stringify(errors);
}

const SUPPRESSED_ERROR_TOAST_PATH_PARTS: readonly string[] = [
  '/api/Accounts/Logout',
  // Public landing assistant (chat + lead): the widget renders its own in-panel
  // message for every failure, and the spec forbids surfacing raw HTTP errors
  // (e.g. a 429 from the anonymous rate limiter) to marketing-site visitors.
  '/api/LandingAssistant/',
];

function shouldSuppressGlobalErrorToast(url: string | null | undefined): boolean {
  const value = url ?? '';
  return SUPPRESSED_ERROR_TOAST_PATH_PARTS.some((part) => value.includes(part));
}

/** HR's Performance controllers (bug-review L4): a refusal there may mean the cached line-manager answer is stale. */
const PERFORMANCE_API = /\/api\/(EmployeeEvaluations|EmployeeGoals|ImprovementPlans|MyPerformanceReviews|Performance[A-Za-z]*)(\/|\?|$)/;

export const handleErrorsInterceptor: HttpInterceptorFn = (req, next) => {
  const injector = inject(Injector);
  const toast = inject(MessageService);
  const translation = inject(Translation);
  const router = inject(Router);

  // The caller renders this failure itself (Tasks SP5c, ruling X16): no toast, and no /unauthorized redirect on a 403.
  if (req.context.get(SILENT_HTTP_ERRORS)) return next(req);

  return next(req).pipe(
    tap({
      next: (event) => {
        if (!(event instanceof HttpResponse)) return;
        const body = event.body;
        if (body && typeof body === 'object' && 'errors' in body && isWorkNestEnvelope(body)) {
          const detail = formatErrors((body as { errors?: unknown }).errors);
          if (!detail) return;
          toast.add({ severity: 'error', summary: translation.instant('COMMON.ERROR'), detail });
        }
      },
      error: (error) => {
        if (error instanceof HttpErrorResponse) {
          if (shouldSuppressGlobalErrorToast(error.url)) {
            return;
          }
          const statusCode = errorStatusCode(error);
          if (statusCode === 401) {
            return;
          }
          if (statusCode === 403) {
            // Bug-review L4: ask HR again whether a cached line manager still is one, so the menu corrects itself (cheap:
            // the service asks only when its cached answer is "line manager").
            if (PERFORMANCE_API.test(req.url)) void injector.get(PerformanceLineManagerService).revalidate();
            const url = router.url?.split('?')[0] ?? '';
            if (url !== '/unauthorized') {
              void router.navigate(['/unauthorized']);
            }
          }
          const detail = extractApiErrorMessage(error, apiErrorFallback(translation));
          toast.add({ severity: 'error', summary: translation.instant('COMMON.ERROR'), detail });
        }
      },
    }),
  );
};
